import {
  Component,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
  type FormEvent,
} from "react";
import {
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  CalendarPlus,
  Car,
  CheckSquare2,
  ChevronRight,
  Download,
  Home,
  Info,
  LogOut,
  MessageSquarePlus,
  Plus,
  RefreshCw,
  Repeat2,
  Settings2,
  UserRound,
  UsersRound,
  X,
} from "lucide-react";
import { api, ApiError } from "./api";
import type {
  AppSettings,
  Delegation,
  FeedbackCategory,
  SessionPayload,
  User,
} from "./types";
import {
  LoginScreen,
  ChangePasswordScreen,
  OnlineStatus,
} from "./screens/Auth";
import { DashboardScreen } from "./screens/Dashboard";
import { CalendarScreen } from "./screens/Calendar";
import { RequestsScreen } from "./screens/Requests";
import { ApprovalsScreen } from "./screens/Approvals";
import { NotificationsScreen } from "./screens/Notifications";
import { syncPushSubscription } from "./push";
import { DirectoryScreen } from "./screens/Directory";
import { TrainingScreen } from "./screens/Training";
import { VehiclesScreen } from "./screens/Vehicles";
import { AboutScreen } from "./screens/About";
import { OnboardingScreen, ProfileScreen } from "./screens/Profile";
import { ManagementScreen } from "./screens/Management";
import { OperationsScreen } from "./screens/Operations";
import { MissionsScreen } from "./screens/Missions";
import { FeedbackScreen } from "./screens/Feedback";
import { BrandLogo } from "./components/BrandLogo";
import {
  Alert,
  Button,
  Field,
  Loading,
  Modal,
  SelectField,
  TextareaField,
  Toast,
} from "./components/ui";
import { dateLabel, initials, roleLabel } from "./utils";
import { DEFAULT_APP_NAME } from "../shared/app-config";
import { lockBodyScroll } from "./dialog-utils";
import { optimizedFeedbackPhoto } from "./feedback-photo";

const defaultSettings: AppSettings = {
  appName: DEFAULT_APP_NAME,
  pushEnabled: false,
};

interface ErrorBoundaryState {
  failed: boolean;
}

class ErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  state: ErrorBoundaryState = { failed: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { failed: true };
  }

  render() {
    return this.state.failed ? (
      <main className="fatal-error">
        <img
          className="fatal-error__logo"
          src="/brand/logo-icon.svg"
          alt=""
          width="72"
          height="72"
        />
        <h1>Sayfa görüntülenemedi</h1>
        <p>
          Beklenmeyen bir arayüz hatası oluştu. Sayfayı yenileyerek tekrar
          deneyin.
        </p>
        <Button onClick={() => window.location.reload()}>
          <RefreshCw aria-hidden="true" />
          Sayfayı yenile
        </Button>
      </main>
    ) : (
      this.props.children
    );
  }
}

function hashState(): { route: string; params: URLSearchParams } {
  const raw = window.location.hash.replace(/^#\/?/, "") || "home";
  const [route = "home", search = ""] = raw.split("?", 2);
  return { route, params: new URLSearchParams(search) };
}

function useHashRoute() {
  const [state, setState] = useState(hashState);

  useEffect(() => {
    const update = () => setState(hashState());
    window.addEventListener("hashchange", update);
    return () => window.removeEventListener("hashchange", update);
  }, []);

  function navigate(path: string) {
    window.location.hash = `/${path}`;
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  return { ...state, navigate };
}

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const feedbackCategories: Array<{
  value: FeedbackCategory;
  label: string;
}> = [
  { value: "BUG", label: "Hata / bug" },
  { value: "SHIFT_CALENDAR", label: "Yanlış vardiya veya takvim" },
  { value: "LEAVE_REQUEST", label: "İzin / talep sorunu" },
  { value: "DESIGN", label: "Tasarım / görünüm" },
  { value: "SUGGESTION", label: "Öneri" },
  { value: "OTHER", label: "Diğer" },
];

function feedbackDeviceType() {
  const agent = navigator.userAgent;
  if (/Android/i.test(agent)) return "Android";
  if (/iPad|iPhone|iPod/i.test(agent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "iOS";
  return "Masaüstü";
}

function FeedbackModal({
  open,
  route,
  onClose,
  notify,
}: {
  open: boolean;
  route: string;
  onClose: () => void;
  notify: (message: string, tone?: "success" | "danger" | "info") => void;
}) {
  const [category, setCategory] = useState<FeedbackCategory>("BUG");
  const [description, setDescription] = useState("");
  const [expectedOutcome, setExpectedOutcome] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCategory("BUG");
    setDescription("");
    setExpectedOutcome("");
    setScreenshot(null);
    setError("");
  }, [open]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!description.trim()) {
      setError("Açıklama zorunludur.");
      return;
    }
    if (screenshot && (!["image/png", "image/jpeg", "image/webp"].includes(screenshot.type) || screenshot.size > 8 * 1024 * 1024)) {
      setError("Ekran görüntüsü PNG, JPEG veya WebP biçiminde ve en fazla 8 MB olmalıdır.");
      return;
    }
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      Boolean(
        (navigator as Navigator & { standalone?: boolean }).standalone,
      );
    setLoading(true);
    try {
      const created = await api<{ id: string }>("/api/feedback", {
        method: "POST",
        body: {
          category,
          description: description.trim(),
          expectedOutcome: expectedOutcome.trim() || null,
          route: `/#/${route}`,
          device: feedbackDeviceType(),
          pwaMode: standalone,
          browser: navigator.userAgent.slice(0, 240),
        },
      });
      if (screenshot) {
        const optimized = await optimizedFeedbackPhoto(screenshot);
        const form = new FormData();
        form.append("screenshot", optimized);
        try {
          await api(`/api/feedback/${created.id}/attachment`, { method: "POST", body: form });
        } catch (caught) {
          onClose();
          notify(caught instanceof ApiError
            ? `Geri bildirim kaydedildi; ekran görüntüsü eklenemedi: ${caught.message}`
            : "Geri bildirim kaydedildi; ekran görüntüsü eklenemedi.", "info");
          return;
        }
      }
      onClose();
      notify("Geri bildiriminiz sistem yöneticisine iletildi.");
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Geri bildirim gönderilemedi.",
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Geri bildirim gönder" wide>
      <form className="form-stack feedback-form" onSubmit={submit}>
        <Alert>
          Parola, geçici PIN, telefon numarası, sağlık bilgisi veya izin
          açıklaması gibi hassas verileri bu forma yazmayın.
        </Alert>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <SelectField
          label="Kategori"
          value={category}
          onChange={(event) =>
            setCategory(event.target.value as FeedbackCategory)
          }
        >
          {feedbackCategories.map((item) => (
            <option value={item.value} key={item.value}>
              {item.label}
            </option>
          ))}
        </SelectField>
        <TextareaField
          label="Açıklama"
          value={description}
          maxLength={2000}
          onChange={(event) => setDescription(event.target.value)}
          required
          hint="Ne yaptığınızı ve ne olduğunu, kişisel veri eklemeden anlatın."
        />
        <TextareaField
          label="Beklenen sonuç"
          value={expectedOutcome}
          maxLength={1000}
          onChange={(event) => setExpectedOutcome(event.target.value)}
          hint="İsteğe bağlı"
        />
        <Field
          type="file"
          label="Ekran görüntüsü (isteğe bağlı)"
          accept="image/png,image/jpeg,image/webp"
          onChange={(event) => setScreenshot(event.target.files?.[0] ?? null)}
          hint={screenshot ? `${screenshot.name} · ${(screenshot.size / 1024 / 1024).toFixed(2)} MB · gönderirken güvenli boyuta küçültülür` : "PNG, JPEG veya WebP · en fazla 8 MB. Görsel 1 MB altına küçültülür ve metadata kaldırılır."}
        />
        <div className="modal-actions">
          <Button type="button" variant="ghost" onClick={onClose}>
            Vazgeç
          </Button>
          <Button type="submit" loading={loading}>
            Geri bildirimi gönder
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function BottomSheet({
  open,
  onClose,
  title,
  description,
  children,
  id,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: ReactNode;
  id: string;
}) {
  const sheetRef = useRef<HTMLElement>(null);
  const closeRef = useRef(onClose);

  useEffect(() => {
    closeRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const releaseBodyLock = lockBodyScroll();
    const focusTimer = window.setTimeout(() => sheetRef.current?.focus({ preventScroll: true }), 0);

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const dialogs = [...document.querySelectorAll<HTMLElement>('[role="dialog"][aria-modal="true"]')];
      if (dialogs.at(-1) !== sheetRef.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current();
        return;
      }
      if (event.key !== "Tab" || !sheetRef.current) return;
      const focusable = [
        ...sheetRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((element) => element.tabIndex >= 0 && element.getClientRects().length > 0 && !element.closest("[hidden],[inert]"));
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) {
        event.preventDefault();
        sheetRef.current.focus({ preventScroll: true });
        return;
      }
      const outside = !sheetRef.current.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || document.activeElement === sheetRef.current || outside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || outside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      releaseBodyLock();
      if (previouslyFocused?.isConnected) previouslyFocused.focus({ preventScroll: true });
    };
  }, [open]);

  if (!open) return null;

  return (
    <div
      className="bottom-sheet-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        ref={sheetRef}
        className="bottom-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-title`}
        aria-describedby={description ? `${id}-description` : undefined}
        tabIndex={-1}
      >
        <span className="bottom-sheet__handle" aria-hidden="true" />
        <header className="bottom-sheet__header">
          <div>
            <h2 id={`${id}-title`}>{title}</h2>
            {description ? (
              <p id={`${id}-description`}>{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            className="icon-button bottom-sheet__close"
            onClick={onClose}
            aria-label="Kapat"
          >
            <X aria-hidden="true" />
          </button>
        </header>
        <div className="bottom-sheet__body">{children}</div>
      </section>
    </div>
  );
}

function AppShell({
  session,
  settings,
  refreshSession,
  refreshSettings,
  onLogout,
}: {
  session: SessionPayload;
  settings: AppSettings;
  refreshSession: () => Promise<void>;
  refreshSettings: () => Promise<void>;
  onLogout: () => Promise<void>;
}) {
  const { route, params, navigate } = useHashRoute();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [newRequestOpen, setNewRequestOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [requestLaunchKey, setRequestLaunchKey] = useState(0);
  const [unread, setUnread] = useState(session.unreadCount ?? 0);
  const [activeDelegation, setActiveDelegation] = useState<Delegation | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    tone: "success" | "danger" | "info";
  } | null>(null);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(
    null,
  );

  const notify = (
    message: string,
    tone: "success" | "danger" | "info" = "success",
  ) => {
    setToast({ message, tone });
    window.setTimeout(() => setToast(null), 4500);
  };

  useEffect(() => {
    const handler = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  useEffect(() => {
    const sync = () => {
      if (document.visibilityState === "visible") {
        void syncPushSubscription(settings.vapidPublicKey).catch(() => {
          // Push isteğe bağlıdır; uygulama içi bildirimleri etkilemez.
        });
      }
    };
    sync();
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", sync);
    return () => {
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", sync);
    };
  }, [settings.vapidPublicKey, session.user.id]);

  useEffect(() => {
    let current = true;
    void api<{ items: Delegation[] }>("/api/delegations")
      .then((result) => {
        if (!current) return;
        setActiveDelegation(result.items.find((delegation) => (
          delegation.currentlyActive
          && String(delegation.delegateUserId) === String(session.user.id)
        )) ?? null);
      })
      .catch(() => {
        if (current) setActiveDelegation(null);
      });
    return () => { current = false; };
  }, [session.user.id]);

  useEffect(() => {
    setAccountMenuOpen(false);
    setNewRequestOpen(false);
  }, [route]);

  const canManage =
    session.user.role === "chief" || session.user.role === "admin"
    || session.user.role === "supervisor" || session.user.role === "manager";
  const isReadOnlyOperationsRole =
    session.user.role === "manager";
  const canViewOperations =
    session.user.role === "supervisor" || isReadOnlyOperationsRole || session.user.role === "admin";

  const navigation = useMemo(
    () => [
      { id: "home", label: "Ana Sayfa", icon: Home, group: "main" },
      { id: "training", label: "Eğitim talepleri", icon: CheckSquare2, group: "main" },
      { id: "vehicles", label: "Araç kontrolü", icon: Car, group: "main" },
      {
        id: "calendar",
        label: "Ekip Takvimi",
        icon: CalendarDays,
        group: "main",
      },
      {
        id: "missions",
        label: "Görevler",
        icon: BriefcaseBusiness,
        group: "main",
      },
      ...(!isReadOnlyOperationsRole
        ? [{
            id: "requests",
            label: "Taleplerim",
            icon: CheckSquare2,
            group: "main",
          }]
        : []),
      ...(canViewOperations
        ? [{
            id: "operations",
            label: "Operasyon",
            icon: BriefcaseBusiness,
            group: "main",
          }]
        : []),
      {
        id: "approvals",
        label: "Onaylar",
        icon: CheckSquare2,
        group: "team",
      },
      {
        id: "directory",
        label: "Rehber",
        icon: UsersRound,
        group: "team",
      },
      {
        id: "notifications",
        label: "Bildirimler",
        icon: Bell,
        group: "account",
      },
      {
        id: "profile",
        label: "Profil",
        icon: UserRound,
        group: "account",
      },
      { id: "about", label: "Sistem Hakkında", icon: Info, group: "account" },
      ...(canManage
        ? [
            {
              id: "management",
              label: "Yönetim",
              icon: Settings2,
              group: "account",
            },
          ]
        : []),
    ],
    [canManage, canViewOperations, isReadOnlyOperationsRole],
  );

  function renderPage() {
    if (route === "about") return <AboutScreen />;
    if (route === "calendar")
      return <CalendarScreen currentUser={session.user} navigate={navigate} />;
    if (route === "missions")
      return <MissionsScreen currentUser={session.user} notify={notify} />;
    if (route === "requests" && !isReadOnlyOperationsRole)
      return (
        <RequestsScreen
          key={`request-launch-${params.get("create") ?? "list"}-${requestLaunchKey}`}
          currentUser={session.user}
          create={
            params.get("create") === "leave" || params.get("create") === "shift"
              ? (params.get("create") as "leave" | "shift")
              : null
          }
          notify={notify}
        />
      );
    if (route === "approvals")
      return <ApprovalsScreen currentUser={session.user} notify={notify} />;
    if (route === "operations" && canViewOperations)
      return <OperationsScreen currentUser={session.user} />;
    if (route === "notifications")
      return (
        <NotificationsScreen
          settings={settings}
          currentUser={session.user}
          navigate={navigate}
          onUnreadChange={setUnread}
          notify={notify}
        />
      );
    if (route === "directory") return <DirectoryScreen currentUser={session.user} />;
    if (route === "training") return <TrainingScreen currentUser={session.user} />;
    if (route === "vehicles") return <VehiclesScreen currentUser={session.user} />;
    if (route === "feedback") return <FeedbackScreen route={params.get("from") ?? "home"} initialId={params.get("id")} notify={notify} />;
    if (route === "profile")
      return (
        <ProfileScreen
          session={session}
          refresh={refreshSession}
          notify={notify}
        />
      );
    if (route === "management" && canManage)
      return (
        <ManagementScreen
          currentUser={session.user}
          settings={settings}
          refreshSettings={refreshSettings}
          notify={notify}
          initialTab={params.get("tab") === "feedback" ? "feedback" : undefined}
          feedbackId={params.get("feedback")}
        />
      );
    return <DashboardScreen session={session} navigate={navigate} />;
  }

  async function install() {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const result = await installPrompt.userChoice;
    if (result.outcome === "accepted") notify("Uygulama ana ekrana ekleniyor.");
    setInstallPrompt(null);
  }

  function openRequest(kind: "leave" | "shift") {
    setNewRequestOpen(false);
    setRequestLaunchKey((current) => current + 1);
    navigate(`requests?create=${kind}`);
  }

  function openMission() {
    setNewRequestOpen(false);
    navigate("missions?create=1");
  }

  const mobileMenuNavigation = navigation.filter((item) =>
    ["missions", "requests", "training", "vehicles", "approvals", "directory", "management", "about"].includes(item.id),
  );

  return (
    <div className="app-shell">
      <OnlineStatus />
      <a className="skip-link" href="#main-content">
        Ana içeriğe geç
      </a>

      <aside className="sidebar" aria-label="Uygulama menüsü">
        <button
          type="button"
          className="sidebar-brand"
          onClick={() => navigate("home")}
          aria-label={`${settings.appName} ana sayfası`}
        >
          <BrandLogo appName={settings.appName} tone="light" subtitle />
        </button>

        <nav className="sidebar-nav" aria-label="Ana menü">
          <p className="sidebar-nav__label">Genel</p>
          {navigation
            .filter((item) => item.group === "main")
            .map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  className={`sidebar-nav__item${route === item.id ? " active" : ""}`}
                  onClick={() => navigate(item.id)}
                  aria-current={route === item.id ? "page" : undefined}
                  key={item.id}
                >
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}

          <p className="sidebar-nav__label">Ekip</p>
          {navigation
            .filter((item) => item.group === "team")
            .map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  className={`sidebar-nav__item${route === item.id ? " active" : ""}`}
                  onClick={() => navigate(item.id)}
                  aria-current={route === item.id ? "page" : undefined}
                  key={item.id}
                >
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                </button>
              );
            })}

          <p className="sidebar-nav__label">Hesap</p>
          {navigation
            .filter((item) => item.group === "account")
            .map((item) => {
              const Icon = item.icon;
              return (
                <button
                  type="button"
                  className={`sidebar-nav__item${route === item.id ? " active" : ""}`}
                  onClick={() => navigate(item.id)}
                  aria-current={route === item.id ? "page" : undefined}
                  key={item.id}
                >
                  <Icon aria-hidden="true" />
                  <span>{item.label}</span>
                  {item.id === "notifications" && unread > 0 ? (
                    <b className="unread-badge">{unread > 99 ? "99+" : unread}</b>
                  ) : null}
                </button>
              );
            })}
        </nav>

        <div className="sidebar-footer">
          {installPrompt ? (
            <Button variant="secondary" onClick={() => void install()}>
              <Download size={17} aria-hidden="true" />
              Uygulamayı yükle
            </Button>
          ) : null}
          <div className="signed-user signed-user--sidebar">
            <button
              type="button"
              className="signed-user__profile"
              onClick={() => navigate("profile")}
              aria-label="Profili aç"
            >
              <span className="avatar avatar--small" aria-hidden="true">
                {initials(session.user.displayName)}
              </span>
              <span className="signed-user__copy">
                <strong>{session.user.displayName}</strong>
                <small>{roleLabel(session.user.role)}</small>
              </span>
            </button>
            <button
              type="button"
              className="signed-user__logout"
              aria-label="Güvenli çıkış yap"
              onClick={() => void onLogout()}
            >
              <LogOut aria-hidden="true" />
            </button>
          </div>
        </div>
      </aside>

      <header className="mobile-header app-topbar">
        <button
          type="button"
          className="mobile-brand"
          onClick={() => navigate("home")}
          aria-label={`${settings.appName} ana sayfası`}
        >
          <BrandLogo appName={settings.appName} tone="light" compact />
        </button>
        <div className="app-topbar__actions">
          <button
            type="button"
            className="notification-button"
            onClick={() => navigate("notifications")}
            aria-label={unread > 0 ? `Bildirimler, ${unread} okunmamış bildirim` : "Bildirimler"}
          >
            <Bell aria-hidden="true" />
            {unread > 0 ? <b>{unread > 99 ? "99+" : unread}</b> : null}
          </button>
          <button
            type="button"
            className="topbar-feedback-button"
            onClick={() => navigate(`feedback?from=${encodeURIComponent(route)}`)}
            aria-haspopup="dialog"
            aria-expanded={feedbackOpen}
            aria-label="Geri bildirim gönder veya geçmişi görüntüle"
          >
            <MessageSquarePlus aria-hidden="true" />
            <span>Geri bildirim</span>
          </button>
          <button
            type="button"
            className="topbar-avatar"
            onClick={() => setAccountMenuOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={accountMenuOpen}
            aria-label="Hesap ve diğer menüleri aç"
          >
            <span className="avatar avatar--small" aria-hidden="true">
              {initials(session.user.displayName)}
            </span>
          </button>
        </div>
      </header>

      <main className="app-content" id="main-content" tabIndex={-1}>
        {activeDelegation ? (
          <div className="delegation-context-banner">
            <Alert tone="success" title="Aktif vekâlet">
              {(activeDelegation.delegatorName ?? "Başteknisyen")} adına vekâleten işlem yapıyorsunuz · {activeDelegation.team} Ekibi · Bitiş {dateLabel(activeDelegation.endDate)}
            </Alert>
          </div>
        ) : null}
        {renderPage()}
      </main>

      <nav className="bottom-nav" aria-label="Mobil ana menü">
        <button
          type="button"
          className={`bottom-nav__item${route === "home" ? " active" : ""}`}
          onClick={() => navigate("home")}
          aria-current={route === "home" ? "page" : undefined}
        >
          <Home aria-hidden="true" />
          <span>Ana Sayfa</span>
        </button>
        <button
          type="button"
          className={`bottom-nav__item${route === "calendar" ? " active" : ""}`}
          onClick={() => navigate("calendar")}
          aria-current={route === "calendar" ? "page" : undefined}
        >
          <CalendarDays aria-hidden="true" />
          <span>Takvim</span>
        </button>
        {isReadOnlyOperationsRole ? (
          <button
            type="button"
            className={`bottom-nav__item bottom-nav__create${route === "operations" ? " active" : ""}`}
            onClick={() => navigate("operations")}
            aria-current={route === "operations" ? "page" : undefined}
          >
            <span className="bottom-nav__create-icon" aria-hidden="true">
              <BriefcaseBusiness />
            </span>
            <span>Operasyon</span>
          </button>
        ) : (
          <button
            type="button"
            className="bottom-nav__item bottom-nav__create"
            onClick={() => setNewRequestOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={newRequestOpen}
          >
            <span className="bottom-nav__create-icon" aria-hidden="true">
              <Plus />
            </span>
            <span>Yeni Talep</span>
          </button>
        )}
        <button
          type="button"
          className={`bottom-nav__item${route === "notifications" ? " active" : ""}`}
          onClick={() => navigate("notifications")}
          aria-current={route === "notifications" ? "page" : undefined}
        >
          <span className="bottom-nav__icon-wrap">
            <Bell aria-hidden="true" />
            {unread > 0 ? <b>{unread > 99 ? "99+" : unread}</b> : null}
          </span>
          <span>Bildirimler</span>
        </button>
        <button
          type="button"
          className={`bottom-nav__item${route === "profile" ? " active" : ""}`}
          onClick={() => navigate("profile")}
          aria-current={route === "profile" ? "page" : undefined}
        >
          <UserRound aria-hidden="true" />
          <span>Profil</span>
        </button>
      </nav>

      {!isReadOnlyOperationsRole ? <BottomSheet
        open={newRequestOpen}
        onClose={() => setNewRequestOpen(false)}
        id="new-request-sheet"
        title="Yeni talep oluştur"
        description="Yapmak istediğiniz işlemi seçin."
      >
        <div className="request-action-list">
          <button
            type="button"
            className="request-action request-action--primary"
            onClick={() => openRequest("leave")}
          >
            <span className="request-action__icon" aria-hidden="true">
              <CalendarPlus />
            </span>
            <span className="request-action__copy">
              <strong>İzin Talebi</strong>
              <small>İzin veya yokluk kaydı oluşturun</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
          {session.user.role !== "supervisor" ? <button
            type="button"
            className="request-action"
            onClick={() => openRequest("shift")}
          >
            <span className="request-action__icon" aria-hidden="true">
              <Repeat2 />
            </span>
            <span className="request-action__copy">
              <strong>Vardiya Değişikliği</strong>
              <small>Tek günlük vardiya değişikliği isteyin</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button> : null}
          <button
            type="button"
            className="request-action"
            onClick={openMission}
          >
            <span className="request-action__icon" aria-hidden="true">
              <BriefcaseBusiness />
            </span>
            <span className="request-action__copy">
              <strong>Görev Kaydı</strong>
              <small>Saha görevi oluşturun veya onaya gönderin</small>
            </span>
            <ChevronRight aria-hidden="true" />
          </button>
        </div>
      </BottomSheet> : null}

      <BottomSheet
        open={accountMenuOpen}
        onClose={() => setAccountMenuOpen(false)}
        id="account-menu-sheet"
        title="Hesap ve diğer işlemler"
      >
        <div className="mobile-account-card">
          <span className="avatar" aria-hidden="true">
            {initials(session.user.displayName)}
          </span>
          <span>
            <strong>{session.user.displayName}</strong>
            <small>{roleLabel(session.user.role)}</small>
          </span>
        </div>

        <nav className="mobile-more-nav" aria-label="Diğer menüler">
          {mobileMenuNavigation.map((item) => {
            const Icon = item.icon;
            return (
              <button
                type="button"
                className={route === item.id ? "active" : ""}
                onClick={() => {
                  setAccountMenuOpen(false);
                  navigate(item.id);
                }}
                aria-current={route === item.id ? "page" : undefined}
                key={item.id}
              >
                <Icon aria-hidden="true" />
                <span>{item.label}</span>
                <ChevronRight aria-hidden="true" />
              </button>
            );
          })}
        </nav>

        <div className="mobile-account-actions">
          {installPrompt ? (
            <Button variant="secondary" onClick={() => void install()}>
              <Download size={17} aria-hidden="true" />
              Uygulamayı yükle
            </Button>
          ) : null}
          <Button variant="ghost" onClick={() => void onLogout()}>
            <LogOut size={17} aria-hidden="true" />
            Güvenli çıkış
          </Button>
        </div>
      </BottomSheet>

      <FeedbackModal
        open={feedbackOpen}
        route={route}
        onClose={() => setFeedbackOpen(false)}
        notify={notify}
      />

      {toast ? (
        <Toast
          message={toast.message}
          tone={toast.tone}
          onClose={() => setToast(null)}
        />
      ) : null}
    </div>
  );
}

export default function App() {
  const [settings, setSettings] = useState<AppSettings>(defaultSettings);
  const [session, setSession] = useState<SessionPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [bootError, setBootError] = useState("");

  useEffect(() => {
    const expired = () => setSession(null);
    window.addEventListener("kompozit:session-expired", expired);
    return () => window.removeEventListener("kompozit:session-expired", expired);
  }, []);

  async function refreshSettings() {
    try {
      const value = await api<AppSettings>("/api/public/settings");
      setSettings({ ...defaultSettings, ...value });
      if (value.appName) document.title = value.appName;
    } catch {
      setSettings(defaultSettings);
    }
  }

  async function refreshSession() {
    try {
      const user = await api<User>("/api/auth/me");
      if (user.mustChangePassword) {
        setSession({ user });
        return;
      }
      const value = await api<SessionPayload>("/api/profile");
      setSession(value);
      if (value.settings)
        setSettings((current) => ({ ...current, ...value.settings }));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) setSession(null);
      else throw caught;
    }
  }

  async function boot() {
    setLoading(true);
    setBootError("");
    const [, sessionResult] = await Promise.allSettled([refreshSettings(), refreshSession()]);
    if (sessionResult.status === "rejected") {
      setBootError(sessionResult.reason instanceof ApiError
        ? sessionResult.reason.message
        : "Oturum bilgisi yüklenemedi. İnternet bağlantınızı kontrol edip yeniden deneyin.");
    }
    setLoading(false);
  }

  useEffect(() => {
    void boot();
  }, []);

  async function logout() {
    try {
      await api("/api/auth/logout", { method: "POST" });
      setSession(null);
      window.location.hash = "/home";
    } catch (caught) {
      window.alert(
        caught instanceof ApiError
          ? caught.message
          : "Güvenli çıkış tamamlanamadı. Bağlantınızı kontrol edin.",
      );
    }
  }

  if (loading)
    return (
      <main className="boot-screen">
        <BrandLogo appName={settings.appName} subtitle />
        <Loading label="Uygulama hazırlanıyor" />
      </main>
    );

  if (bootError)
    return (
      <main className="boot-screen">
        <BrandLogo appName={settings.appName} subtitle />
        <Alert tone="danger" title="Uygulama yüklenemedi">{bootError}</Alert>
        <Button onClick={() => void boot()}><RefreshCw size={18} />Güvenli biçimde yeniden dene</Button>
      </main>
    );

  if (!session)
    return (
      <ErrorBoundary>
        <LoginScreen settings={settings} onLogin={refreshSession} />
      </ErrorBoundary>
    );

  if (session.user.mustChangePassword)
    return (
      <ErrorBoundary>
        <ChangePasswordScreen settings={settings} onDone={refreshSession} />
      </ErrorBoundary>
    );

  if (session.user.onboardingRequired)
    return (
      <ErrorBoundary>
        <OnboardingScreen session={session} onDone={refreshSession} />
      </ErrorBoundary>
    );

  return (
    <ErrorBoundary>
      <AppShell
        session={session}
        settings={settings}
        refreshSession={refreshSession}
        refreshSettings={refreshSettings}
        onLogout={logout}
      />
    </ErrorBoundary>
  );
}
